import React from "react";
import { CLASSES_AND_SPECS } from "lib/classes-and-specs";
import { convertDisplayNameToDataName, getFactionFromNum, getFormattedNames } from "lib/wowHelpers";
import { Link, useLocation } from "react-router-dom";
import RosterBlockTalents from "./RosterBlockTalents";
import { kFormatter } from "lib/tierHelper";
import { ReactComponent as EchoLogo } from "svg/echoLogo.svg";
import { getWowStatic } from "hooks/wow/getStaticData";
import WowTooltip from "components/Tooltips/WowTooltip";
import { capitalizeFirstLetter } from "lib/general-helper";

const RosterBlock = ({ personalData, armoryData, wclData = [] }) => {
  const { search } = useLocation();
  const hasWclData = wclData.length > 0;
  const toonWclData = wclData.filter((data) => data.name.toLowerCase() === personalData.rwfToon.toLowerCase()).slice(-1)[0];
  const toonArmoryData = armoryData.filter((data) => data?.profile_name?.toLowerCase() === personalData?.toon?.toLowerCase())[0];

  if ((hasWclData && !toonWclData) || (!hasWclData && !toonArmoryData)) return null;

  const classType = hasWclData
    ? convertDisplayNameToDataName(toonWclData?.class)
    : convertDisplayNameToDataName(toonArmoryData?.class);
  const specType = hasWclData
    ? convertDisplayNameToDataName(toonWclData?.spec)
    : convertDisplayNameToDataName(toonArmoryData?.spec);
  const faction = hasWclData ? getFactionFromNum(toonWclData?.faction) : toonArmoryData?.faction;
  const selectedTalents = hasWclData ? toonWclData?.talents : toonArmoryData?.all_talents;
  const { displayClassName, displaySpecName, dataClassName, dataSpecName } = getFormattedNames(classType, specType);
  const classColor = CLASSES_AND_SPECS.filter((data) => data.class === displayClassName)[0]?.color;
  const factionColor = faction === "alliance" ? "rgba(0, 120, 255, 0.25)" : "rgba(197, 24, 24, 0.25)";
  const dpsHpsStat = personalData.role === "healer" ? toonWclData?.hps : toonWclData?.dps;

  // Calling getWowStatic with 'none' will return without actually making the data call
  // I need to do this because the same amount of hook calls have to be made in each render
  const trinket1Data = hasWclData ? getWowStatic("item", toonWclData?.trinket1?.item) : getWowStatic("none", 0);
  const trinket2Data = hasWclData ? getWowStatic("item", toonWclData?.trinket2?.item) : getWowStatic("none", 0);

  return (
    <div className="roster-block">
      <div className="faction-icon-container" style={{ background: factionColor }}>
        <img src={`https://static.bigbrain.gg/wow/assets/wow-${faction}.svg`} alt={faction} className="faction-icon" />
      </div>
      <div className="top-section">
        <div className="block-header">
          <div className="image-square" style={{ border: `1px solid ${classColor}` }}>
            <img src={personalData.img} alt={personalData.name} className="profile-img" />
          </div>
          <div className="title-and-subtitle-container">
            <div className="title" style={{ color: classColor }}>
              <img
                src={`https://static.bigbrain.gg/wow/spec-icons/${dataClassName}-${dataSpecName}.jpg`}
                alt={`${displaySpecName} ${displayClassName}`}
                className="spec-class-icon"
              />
              {capitalizeFirstLetter(personalData.name)}
            </div>
            <div className="subtitle">
              {displaySpecName.toUpperCase()} {displayClassName.toUpperCase()}
            </div>
          </div>
        </div>
        {hasWclData && trinket1Data && trinket2Data ? (
          <div className="encounter-data-container">
            <div className="encounter-data-block">
              <div className="encounter-data">
                {toonWclData.item_level}
                <div className="label">Item Level</div>
              </div>
            </div>
            <div className="divider" />
            <div className="encounter-data-block">
              <div className="encounter-data">
                {kFormatter(dpsHpsStat)}
                <div className="label">{personalData.role === "healer" ? "HPS" : "DPS"}</div>
              </div>
            </div>
            <div className="divider" />
            <div className="trinkets-block">
              <div className="trinkets-container">
                <WowTooltip type="item" id={toonWclData?.trinket1?.item}>
                  <div className="trinket-border">
                    <img src={trinket1Data?.img} alt={trinket1Data?.name} className="trinket-img" />
                  </div>
                </WowTooltip>
                <WowTooltip type="item" id={toonWclData?.trinket2?.item}>
                  <div className="trinket-border">
                    <img src={trinket2Data?.img} alt={trinket2Data?.name} className="trinket-img" />
                  </div>
                </WowTooltip>
              </div>
              <div className="label">Trinkets</div>
            </div>
          </div>
        ) : (
          <div className="bio">{personalData.bio}</div>
        )}
        {selectedTalents && <RosterBlockTalents classType={classType} specType={specType} talents={selectedTalents} />}
      </div>
      <Link
        to={{
          pathname: `/wow/echo/${personalData.name.toLowerCase()}`,
          search,
        }}
      >
        <div className="view-profile-button">
          <EchoLogo className="mini-echo-logo" /> View Pro Build
        </div>
      </Link>
    </div>
  );
};

export default RosterBlock;
